import React, { ReactNode, RefCallback } from "react";
import type { CrudModalField } from "@/components/tables/fields/crudModalField";
import CrudCheckboxField from "@/components/tables/fields/CrudCheckboxField";
import CrudPhoneField from "@/components/tables/fields/CrudPhoneField";
import CrudDateTimeField from "@/components/tables/fields/CrudDateTimeField";
import CrudDateField from "@/components/tables/fields/CrudDateField";
import CrudTextareaField from "@/components/tables/fields/CrudTextareaField";
import CrudTextField from "@/components/tables/fields/CrudTextField";
import CrudJsonTextareaField from "@/components/tables/fields/CrudJsonTextareaField";
import CrudJsonBuilderField from "@/components/tables/fields/CrudJsonBuilderField";
import CrudJsonEditorField from "@/components/tables/fields/CrudJsonEditorField";
import CrudCreatableSelectField from "@/components/tables/fields/CrudCreatableSelectField";
import CrudSubjectSearchField from "@/components/tables/fields/CrudSubjectSearchField";
import CrudAsyncEntitySearchField from "@/components/tables/fields/CrudAsyncEntitySearchField";
import CrudSelectField from "@/components/tables/fields/CrudSelectField";
import { isPhoneField } from "@/components/tables/fields/fieldNameRules";

export interface CrudFieldProps {
  field: CrudModalField;
  registerValidationTarget?: (name: string) => RefCallback<HTMLElement>;
  entityOfTable?: string;
  convertsPriceToEurocent?: boolean;
  companySubjectId?: string;
  onChange?: (value: any) => void;
  modalMode?: string;
  userEmails?: string[];
  subjectIdForBuildings?: any;
  additionalComponentForModal?: ReactNode;
  limit?: string;
  ignoreGenericCrudSelectMaxWidth?: boolean;
  /** @deprecated provide the handler via CrudErrorContext instead */
  handleError?: (error: any) => void;
}

const CrudField = (props: CrudFieldProps) => {
  const { field } = props;
  const validationTargetRef = field.id
    ? props.registerValidationTarget?.(field.id)
    : undefined;

  if (
    field.replaceWithAdditionalComponentForModal &&
    props.additionalComponentForModal
  ) {
    return <div ref={validationTargetRef}>{props.additionalComponentForModal}</div>;
  }

  if (field.asyncEntitySearch && field.entity) {
    return (
      <CrudAsyncEntitySearchField
        field={field}
        companySubjectId={props.companySubjectId}
        subjectIdForBuildings={props.subjectIdForBuildings}
        limit={props.limit}
        validationTargetRef={validationTargetRef}
        onChange={props.onChange}
        handleError={props.handleError}
      />
    );
  }
  if (field.entity || (field.selectOptions && !field.createAble)) {
    return (
      <CrudSelectField
        field={field}
        entityOfTable={props.entityOfTable}
        modalMode={props.modalMode}
        companySubjectId={props.companySubjectId}
        subjectIdForBuildings={props.subjectIdForBuildings}
        limit={props.limit}
        ignoreGenericCrudSelectMaxWidth={props.ignoreGenericCrudSelectMaxWidth}
        validationTargetRef={validationTargetRef}
        onChange={props.onChange}
        handleError={props.handleError}
      />
    );
  }
  if (field.createAble && field.selectOptions) {
    return (
      <CrudCreatableSelectField
        field={field}
        validationTargetRef={validationTargetRef}
        onChange={props.onChange}
      />
    );
  }
  if (field.asyncDropDown) {
    return (
      <CrudSubjectSearchField
        field={field}
        entityOfTable={props.entityOfTable}
        validationTargetRef={validationTargetRef}
        onChange={props.onChange}
      />
    );
  }
  if (field.isJson) {
    return (
      <CrudJsonTextareaField
        field={field}
        modalMode={props.modalMode}
        registerValidationTarget={props.registerValidationTarget}
        onChange={props.onChange}
      />
    );
  }
  if (field.useJsonBuilder) {
    return (
      <CrudJsonBuilderField
        field={field}
        modalMode={props.modalMode}
        validationTargetRef={validationTargetRef}
        onChange={props.onChange}
      />
    );
  }
  if (field.useJsonEditor) {
    return (
      <CrudJsonEditorField
        field={field}
        validationTargetRef={validationTargetRef}
        onChange={props.onChange}
      />
    );
  }
  if (field.checkBox) {
    return (
      <CrudCheckboxField
        field={field}
        entityOfTable={props.entityOfTable}
        validationTargetRef={validationTargetRef}
        onChange={props.onChange}
      />
    );
  }
  if (field.datePicker) {
    return (
      <CrudDateField
        field={field}
        entityOfTable={props.entityOfTable}
        validationTargetRef={validationTargetRef}
        onChange={props.onChange}
      />
    );
  }
  if (field.dateTimePicker) {
    return (
      <CrudDateTimeField
        field={field}
        entityOfTable={props.entityOfTable}
        validationTargetRef={validationTargetRef}
        onChange={props.onChange}
      />
    );
  }
  if (field.textarea === true) {
    return (
      <CrudTextareaField
        field={field}
        modalMode={props.modalMode}
        registerValidationTarget={props.registerValidationTarget}
        additionalComponentForModal={props.additionalComponentForModal}
        onChange={props.onChange}
      />
    );
  }
  if (isPhoneField(field.id)) {
    return (
      <CrudPhoneField
        field={field}
        validationTargetRef={validationTargetRef}
        onChange={props.onChange}
      />
    );
  }
  return (
    <CrudTextField
      field={field}
      entityOfTable={props.entityOfTable}
      convertsPriceToEurocent={props.convertsPriceToEurocent}
      modalMode={props.modalMode}
      userEmails={props.userEmails}
      registerValidationTarget={props.registerValidationTarget}
      onChange={props.onChange}
    />
  );
};

export default CrudField;
